import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FlowService } from "../../../../../service/FlowService";
import { FluxService } from "../../../../../service/FluxService";
import { FormsService } from "../../../../../service/FormService";
import { AuthService } from "../../../../../service/AuthService";
import { useAuthStore } from "../../../../../store/storeAuth";
import { useNotificationStore, NotificationType } from "../../../../../store/storeNotifications";
import useFlowsStore from "../../../../../store/storeFlows";
import { getAPIErrorMessage } from "../../../../../helpers/helpers";
import { CreateItemType } from "../../types";
import { convertShorthandToReactFlow } from "../../../../CreateShorthandFlowModal";

interface UseItemHandlersProps {
  type: CreateItemType;
  name: string;
  selectedSpaceId: string;
  ensureFolderExists: (folderName: string, spaceId?: string) => Promise<string | null>;
  loadSpaces: () => Promise<void>;
  resetForm: () => void;
  onHide: () => void;
  setVectorMemorySpaceId: (id: string | undefined) => void;
  setShowVectorModal: (show: boolean) => void;
  setIsCreating: (creating: boolean) => void;
  setIsCreateFormLoading: (loading: boolean) => void;
  setShowInterfaceModal: (show: boolean) => void;
  expandCreateWithAi?: boolean;
  userPrompt?: string;
  setIsCreatingFromShorthand?: (creating: boolean) => void;
  initialFolderId?: string;
}

export const useItemHandlers = ({
  type,
  name,
  selectedSpaceId,
  ensureFolderExists,
  loadSpaces,
  resetForm,
  onHide,
  setVectorMemorySpaceId,
  setShowVectorModal,
  setIsCreating,
  setIsCreateFormLoading,
  setShowInterfaceModal,
  expandCreateWithAi,
  userPrompt,
  setIsCreatingFromShorthand,
  initialFolderId,
}: UseItemHandlersProps) => {
  const navigate = useNavigate();
  const { addFlow } = useFlowsStore((state) => state);
  const { setAuthUser: setAuthUserStore } = useAuthStore((state) => state);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const handleCreateAgent = useCallback(async () => {
    if (!name.trim()) {
      setNotification({
        type: NotificationType.Error,
        message: "Please enter a name",
      });
      return;
    }

    setIsCreating(true);
    try {
      const folderId = initialFolderId
        ? initialFolderId
        : await ensureFolderExists(
            "Agents",
            selectedSpaceId === "private" ? undefined : selectedSpaceId,
          );
      if (!folderId) {
        setIsCreating(false);
        return;
      }

      const response = await FlowService.createFlow({
        name: name.trim(),
        folderId: folderId,
        type: "flow",
      });

      addFlow(response.data);
      await loadSpaces();
      const userRes = await AuthService.getMe();
      setAuthUserStore(userRes?.data);

      window.dispatchEvent(new CustomEvent("sidebar-refresh"));

      setNotification({
        type: NotificationType.Success,
        message: "Agent created successfully",
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      navigate(`/dashboard-beta/agent/${response.data.id}`);
      onHide();
      resetForm();
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as any),
      });
    } finally {
      setIsCreating(false);
    }
  }, [
    name,
    selectedSpaceId,
    initialFolderId,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    onHide,
    setIsCreating,
    addFlow,
    setAuthUserStore,
    setNotification,
    navigate,
  ]);

  const handleCreateAgentWithAi = useCallback(async () => {
    if (!name.trim()) {
      setNotification({
        type: NotificationType.Error,
        message: "Agent name is required",
      });
      return;
    }
    setIsCreatingFromShorthand?.(true);
    try {
      const autoCreatorFlowId = import.meta.env.VITE_AUTO_CREATOR_FLOW_ID || "";
      if (!autoCreatorFlowId) {
        setNotification({
          type: NotificationType.Error,
          message: "Auto creator agent ID is not configured",
        });
        setIsCreatingFromShorthand?.(false);
        return;
      }
      const variableInputs = [
        {
          inputId: "varInputNode_1749483673977_0.5071",
          inputText: userPrompt ?? "",
          links: [],
          files: [],
        },
      ];
      const autoCreatorResponse = await FluxService.FluxApiProduction(
        autoCreatorFlowId,
        { variableInputs },
      );
      if (
        autoCreatorResponse?.data?.message &&
        Array.isArray(autoCreatorResponse.data.message)
      ) {
        const processedOutput = autoCreatorResponse.data.message.find(
          (output: { outputId?: string; text?: string }) =>
            output.outputId && output.text && output.text.trim() !== "",
        );
        if (!processedOutput || !processedOutput.text) {
          throw new Error("Auto creator did not return valid output");
        }
        const generatedShorthandJson = processedOutput.text;
        try {
          const parsedJson = JSON.parse(generatedShorthandJson);
          if (!parsedJson.nodes || !parsedJson.edges) {
            throw new Error("Generated JSON is missing nodes or edges");
          }
          const nodesArray = Array.isArray(parsedJson.nodes)
            ? parsedJson.nodes
            : [];
          const edgesArray = Array.isArray(parsedJson.edges)
            ? parsedJson.edges
            : [];
          const hasShorthandEdges = edgesArray.some(
            (edge: { connection?: { from?: unknown; to?: unknown } }) =>
              edge?.connection?.from && edge?.connection?.to,
          );
          const hasReactFlowEdges = edgesArray.some(
            (edge: { source?: unknown; target?: unknown }) =>
              typeof edge?.source === "string" && typeof edge?.target === "string",
          );
          const isReactFlowJson = !hasShorthandEdges || hasReactFlowEdges;
          const folderId = initialFolderId
            ? initialFolderId
            : await ensureFolderExists(
                "Agents",
                selectedSpaceId === "private" ? undefined : selectedSpaceId,
              );
          if (!folderId) {
            setIsCreatingFromShorthand?.(false);
            return;
          }
          const createResponse = await FlowService.createFlow({
            name: name.trim(),
            folderId,
            type: "flow",
          });
          const flowData = isReactFlowJson
            ? parsedJson
            : convertShorthandToReactFlow({
                ...parsedJson,
                nodes: nodesArray,
                edges: edgesArray,
              });
          await FlowService.nodesAutomation(createResponse.data.id, flowData);
          addFlow({
            id: createResponse.data.id,
            name: createResponse.data.name,
            index: createResponse.data.index,
          });
          await loadSpaces();
          const userRes = await AuthService.getMe();
          setAuthUserStore(userRes?.data);
          window.dispatchEvent(new CustomEvent("sidebar-refresh"));
          setNotification({
            type: NotificationType.Success,
            message: "Agent created successfully",
          });
          resetForm();
          onHide();
          await new Promise((resolve) => setTimeout(resolve, 100));
          navigate(`/dashboard-beta/agent/${createResponse.data.id}`);
        } catch (jsonError) {
          throw new Error("Auto creator did not return valid JSON");
        }
      } else {
        throw new Error("Auto creator response format is invalid");
      }
    } catch (error) {
      let errorMessage = "Failed to create agent";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        typeof (error as { response?: { data?: { message?: string } } }).response
          ?.data?.message === "string"
      ) {
        errorMessage = (error as { response: { data: { message: string } } })
          .response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setNotification({
        type: NotificationType.Error,
        message: errorMessage,
      });
    } finally {
      setIsCreatingFromShorthand?.(false);
    }
  }, [
    name,
    userPrompt,
    selectedSpaceId,
    initialFolderId,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    onHide,
    addFlow,
    setAuthUserStore,
    setNotification,
    navigate,
    setIsCreatingFromShorthand,
  ]);

  const handleCreateFluxbot = useCallback(async () => {
    if (!name.trim()) {
      setNotification({
        type: NotificationType.Error,
        message: "Please enter a name",
      });
      return;
    }

    setIsCreating(true);
    try {
      const spaceId = selectedSpaceId === "private" ? undefined : selectedSpaceId;
      const folderId = await ensureFolderExists("Fluxbots", spaceId);
      if (!folderId) {
        setIsCreating(false);
        return;
      }

      const response = await FlowService.createFlow({
        name: name.trim(),
        folderId: folderId,
        type: "chat",
      });

      addFlow(response.data);
      await loadSpaces();
      const userRes = await AuthService.getMe();
      setAuthUserStore(userRes?.data);

      window.dispatchEvent(new CustomEvent("sidebar-refresh"));

      setNotification({
        type: NotificationType.Success,
        message: "Fluxbot created successfully",
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      navigate(`/dashboard-beta/agent/${response.data.id}`);
      onHide();
      resetForm();
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as any),
      });
    } finally {
      setIsCreating(false);
    }
  }, [
    name,
    selectedSpaceId,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    onHide,
    setIsCreating,
    addFlow,
    setAuthUserStore,
    setNotification,
    navigate,
  ]);

  const handleCreateVectorMemory = useCallback(async () => {
    const actualSpaceId =
      selectedSpaceId === "private" || selectedSpaceId === ""
        ? undefined
        : selectedSpaceId;
    setVectorMemorySpaceId(actualSpaceId);
    setShowVectorModal(true);
    resetForm();
  }, [
    selectedSpaceId,
    resetForm,
    setVectorMemorySpaceId,
    setShowVectorModal,
  ]);

  const handleCreateInterface = useCallback(
    async (
      interfaceName: string,
      useAgent: boolean,
      flowId: string,
      spaceId?: string,
    ) => {
      setIsCreateFormLoading(true);
      try {
        const actualSpaceId = spaceId === "private" ? undefined : spaceId;
        const folderId = await ensureFolderExists("Agents", actualSpaceId);
        if (!folderId) {
          setIsCreateFormLoading(false);
          return;
        }

        const formResponse = await FormsService.createForm({
          name: interfaceName.trim(),
          flowId: flowId,
          folderId: folderId,
          useAgent,
        });

        await loadSpaces();
        const userRes = await AuthService.getMe();
        setAuthUserStore(userRes?.data);

        window.dispatchEvent(new CustomEvent("sidebar-refresh"));

        setNotification({
          type: NotificationType.Success,
          message: "Interface created successfully",
        });

        await new Promise((resolve) => setTimeout(resolve, 300));
        navigate(`/dashboard-beta/interface/${formResponse.data.id}`);
        setShowInterfaceModal(false);
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      } finally {
        setIsCreateFormLoading(false);
      }
    },
    [
      ensureFolderExists,
      loadSpaces,
      setShowInterfaceModal,
      setIsCreateFormLoading,
      setAuthUserStore,
      setNotification,
      navigate,
    ],
  );

  const handleCreate = useCallback(async () => {
    if (type === "agent") {
      if (expandCreateWithAi && userPrompt?.trim()) {
        await handleCreateAgentWithAi();
      } else {
        await handleCreateAgent();
      }
    } else if (type === "fluxbot") {
      await handleCreateFluxbot();
    } else if (type === "vectorMemory") {
      await handleCreateVectorMemory();
    } else if (type === "interface") {
      setShowInterfaceModal(true);
    }
  }, [
    type,
    expandCreateWithAi,
    userPrompt,
    handleCreateAgent,
    handleCreateAgentWithAi,
    handleCreateFluxbot,
    handleCreateVectorMemory,
    setShowInterfaceModal,
  ]);

  return {
    handleCreate,
    handleCreateInterface,
  };
};
